/**
 * Inline SVG icon set for the "Presence" re-skin. Lucide/feather geometry
 * (24×24, round caps), drawn in `currentColor` and sized to `1em` so the parent
 * controls size (font-size, in cqw) and colour. Purely presentational.
 */

import type { ReactNode } from 'react';

type IconProps = { title?: string };

function Stroke({ children, title }: { children: ReactNode; title?: string }): JSX.Element {
    return (
        <svg
            className = "ic"
            viewBox = "0 0 24 24"
            fill = "none"
            stroke = "currentColor"
            strokeWidth = { 2 }
            strokeLinecap = "round"
            strokeLinejoin = "round"
            role = { title ? 'img' : 'presentation' }
            aria-label = { title }
            aria-hidden = { title ? undefined : true }>
            { children }
        </svg>
    );
}

function Fill({ children, title }: { children: ReactNode; title?: string }): JSX.Element {
    return (
        <svg
            className = "ic"
            viewBox = "0 0 24 24"
            fill = "currentColor"
            role = { title ? 'img' : 'presentation' }
            aria-label = { title }
            aria-hidden = { title ? undefined : true }>
            { children }
        </svg>
    );
}

export const Mic = (p: IconProps): JSX.Element => (
    <Stroke { ...p }>
        <path d = "M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
        <path d = "M19 10v2a7 7 0 0 1-14 0v-2" />
        <line x1 = "12" y1 = "19" x2 = "12" y2 = "22" />
    </Stroke>
);

export const MicOff = (p: IconProps): JSX.Element => (
    <Stroke { ...p }>
        <line x1 = "2" y1 = "2" x2 = "22" y2 = "22" />
        <path d = "M18.89 13.23A7 7 0 0 0 19 12v-2" />
        <path d = "M5 10v2a7 7 0 0 0 12 5" />
        <path d = "M15 9.34V5a3 3 0 0 0-5.68-1.33" />
        <path d = "M9 9v3a3 3 0 0 0 5.12 2.12" />
        <line x1 = "12" y1 = "19" x2 = "12" y2 = "22" />
    </Stroke>
);

export const Video = (p: IconProps): JSX.Element => (
    <Stroke { ...p }>
        <path d = "m22 8-6 4 6 4V8Z" />
        <rect x = "2" y = "6" width = "14" height = "12" rx = "2" />
    </Stroke>
);

export const VideoOff = (p: IconProps): JSX.Element => (
    <Stroke { ...p }>
        <path d = "M10.66 6H14a2 2 0 0 1 2 2v2.34l1 1L22 8v8" />
        <path d = "M16 16a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h2l10 10Z" />
        <line x1 = "2" y1 = "2" x2 = "22" y2 = "22" />
    </Stroke>
);

export const Monitor = (p: IconProps): JSX.Element => (
    <Stroke { ...p }>
        <rect x = "2" y = "3" width = "20" height = "14" rx = "2" />
        <line x1 = "8" y1 = "21" x2 = "16" y2 = "21" />
        <line x1 = "12" y1 = "17" x2 = "12" y2 = "21" />
    </Stroke>
);

export const ScreenShare = (p: IconProps): JSX.Element => (
    <Stroke { ...p }>
        <path d = "M13 3H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-3" />
        <line x1 = "8" y1 = "21" x2 = "16" y2 = "21" />
        <line x1 = "12" y1 = "17" x2 = "12" y2 = "21" />
        <path d = "m17 8 5-5" />
        <path d = "M17 3h5v5" />
    </Stroke>
);

export const Hand = (p: IconProps): JSX.Element => (
    <Stroke { ...p }>
        <path d = "M18 11V6a2 2 0 0 0-2-2 2 2 0 0 0-2 2" />
        <path d = "M14 10V4a2 2 0 0 0-2-2 2 2 0 0 0-2 2v2" />
        <path d = "M10 10.5V6a2 2 0 0 0-2-2 2 2 0 0 0-2 2v8" />
        <path d = "M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15" />
    </Stroke>
);

export const Smile = (p: IconProps): JSX.Element => (
    <Stroke { ...p }>
        <circle cx = "12" cy = "12" r = "10" />
        <path d = "M8 14s1.5 2 4 2 4-2 4-2" />
        <line x1 = "9" y1 = "9" x2 = "9.01" y2 = "9" />
        <line x1 = "15" y1 = "9" x2 = "15.01" y2 = "9" />
    </Stroke>
);

export const Chat = (p: IconProps): JSX.Element => (
    <Stroke { ...p }>
        <path d = "M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2Z" />
    </Stroke>
);

export const Board = (p: IconProps): JSX.Element => (
    <Stroke { ...p }>
        <path d = "M2 3h20" />
        <path d = "M21 3v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V3" />
        <path d = "m7 21 5-5 5 5" />
    </Stroke>
);

export const Captions = (p: IconProps): JSX.Element => (
    <Stroke { ...p }>
        <rect x = "2" y = "5" width = "20" height = "14" rx = "2" />
        <path d = "M7 15a2.5 2.5 0 0 1 0-5c.6 0 1.1.2 1.5.5" />
        <path d = "M14.5 15a2.5 2.5 0 0 1 0-5c.6 0 1.1.2 1.5.5" />
    </Stroke>
);

export const Tile = (p: IconProps): JSX.Element => (
    <Stroke { ...p }>
        <rect x = "3" y = "3" width = "7" height = "7" rx = "1" />
        <rect x = "14" y = "3" width = "7" height = "7" rx = "1" />
        <rect x = "3" y = "14" width = "7" height = "7" rx = "1" />
        <rect x = "14" y = "14" width = "7" height = "7" rx = "1" />
    </Stroke>
);

export const Pin = (p: IconProps): JSX.Element => (
    <Stroke { ...p }>
        <path d = "M12 17v5" />
        <path d = "M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1Z" />
    </Stroke>
);

export const SpeakerView = (p: IconProps): JSX.Element => (
    <Stroke { ...p }>
        <rect x = "3" y = "4" width = "18" height = "12" rx = "2" />
        <rect x = "3" y = "18" width = "5" height = "3" rx = "1" />
        <rect x = "10" y = "18" width = "5" height = "3" rx = "1" />
        <rect x = "17" y = "18" width = "4" height = "3" rx = "1" />
    </Stroke>
);

export const More = (p: IconProps): JSX.Element => (
    <Fill { ...p }>
        <circle cx = "5" cy = "12" r = "1.8" />
        <circle cx = "12" cy = "12" r = "1.8" />
        <circle cx = "19" cy = "12" r = "1.8" />
    </Fill>
);

export const PhoneOff = (p: IconProps): JSX.Element => (
    <Stroke { ...p }>
        <path d = "M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.42 19.42 0 0 1-3.33-2.67m-2.67-3.34a19.79 19.79 0 0 1-3.07-8.63A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91" />
        <line x1 = "22" y1 = "2" x2 = "2" y2 = "22" />
    </Stroke>
);

// The conventional "end call" glyph: a handset rotated on a downward arc (Material
// `call_end`). Reads as "hang up" without the PhoneOff strikethrough, which looks like
// "phone disabled" rather than "leave the meeting".
export const Hangup = (p: IconProps): JSX.Element => (
    <Fill { ...p }>
        <path d = "M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85-.18.18-.43.28-.7.28-.28 0-.53-.11-.71-.29L.29 13.08c-.18-.17-.29-.42-.29-.7 0-.28.11-.53.29-.71C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.67c.18.18.29.43.29.71 0 .28-.11.53-.29.71l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.11-.7-.28-.79-.74-1.69-1.36-2.67-1.85-.33-.16-.56-.5-.56-.9v-3.1C15.15 9.25 13.6 9 12 9Z" />
    </Fill>
);

export const Calendar = (p: IconProps): JSX.Element => (
    <Stroke { ...p }>
        <rect x = "3" y = "4" width = "18" height = "18" rx = "2" />
        <line x1 = "16" y1 = "2" x2 = "16" y2 = "6" />
        <line x1 = "8" y1 = "2" x2 = "8" y2 = "6" />
        <line x1 = "3" y1 = "10" x2 = "21" y2 = "10" />
    </Stroke>
);

export const ArrowRight = (p: IconProps): JSX.Element => (
    <Stroke { ...p }>
        <line x1 = "5" y1 = "12" x2 = "19" y2 = "12" />
        <polyline points = "12 5 19 12 12 19" />
    </Stroke>
);

export const Phone = (p: IconProps): JSX.Element => (
    <Stroke { ...p }>
        <path d = "M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92Z" />
    </Stroke>
);

export const QrCode = (p: IconProps): JSX.Element => (
    <Stroke { ...p }>
        <rect x = "3" y = "3" width = "7" height = "7" rx = "1" />
        <rect x = "14" y = "3" width = "7" height = "7" rx = "1" />
        <rect x = "3" y = "14" width = "7" height = "7" rx = "1" />
        <line x1 = "14" y1 = "14" x2 = "14" y2 = "17" />
        <line x1 = "17" y1 = "14" x2 = "21" y2 = "14" />
        <line x1 = "21" y1 = "17" x2 = "21" y2 = "21" />
        <line x1 = "14" y1 = "21" x2 = "17" y2 = "21" />
    </Stroke>
);

export const Users = (p: IconProps): JSX.Element => (
    <Stroke { ...p }>
        <path d = "M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
        <circle cx = "9" cy = "7" r = "4" />
        <path d = "M22 21v-2a4 4 0 0 0-3-3.87" />
        <path d = "M16 3.13a4 4 0 0 1 0 7.75" />
    </Stroke>
);

export const Lock = (p: IconProps): JSX.Element => (
    <Stroke { ...p }>
        <rect x = "3" y = "11" width = "18" height = "11" rx = "2" />
        <path d = "M7 11V7a5 5 0 0 1 10 0v4" />
    </Stroke>
);

export const Check = (p: IconProps): JSX.Element => (
    <Stroke { ...p }>
        <polyline points = "20 6 9 17 4 12" />
    </Stroke>
);

export const X = (p: IconProps): JSX.Element => (
    <Stroke { ...p }>
        <line x1 = "18" y1 = "6" x2 = "6" y2 = "18" />
        <line x1 = "6" y1 = "6" x2 = "18" y2 = "18" />
    </Stroke>
);

export const Record = (p: IconProps): JSX.Element => (
    <Fill { ...p }>
        <circle cx = "12" cy = "12" r = "6" />
    </Fill>
);

export const Settings = (p: IconProps): JSX.Element => (
    <Stroke { ...p }>
        <path d = "M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2Z" />
        <circle cx = "12" cy = "12" r = "3" />
    </Stroke>
);

export const Info = (p: IconProps): JSX.Element => (
    <Stroke { ...p }>
        <circle cx = "12" cy = "12" r = "10" />
        <line x1 = "12" y1 = "16" x2 = "12" y2 = "12" />
        <line x1 = "12" y1 = "8" x2 = "12.01" y2 = "8" />
    </Stroke>
);

export const Keypad = (p: IconProps): JSX.Element => (
    <Fill { ...p }>
        <circle cx = "6" cy = "6" r = "1.7" />
        <circle cx = "12" cy = "6" r = "1.7" />
        <circle cx = "18" cy = "6" r = "1.7" />
        <circle cx = "6" cy = "12" r = "1.7" />
        <circle cx = "12" cy = "12" r = "1.7" />
        <circle cx = "18" cy = "12" r = "1.7" />
        <circle cx = "6" cy = "18" r = "1.7" />
        <circle cx = "12" cy = "18" r = "1.7" />
        <circle cx = "18" cy = "18" r = "1.7" />
    </Fill>
);
